import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClassroomsList,
  ContentProvider,
  CourseContentList,
  CoursesUnitsList,
  CoursesUnitsScreen,
  CourseUnit,
  ExternalContentProvider,
  ImportProvider,
  TableKeyValue,
} from '../coursesunits/modeles/coursesUnits.modele';
import { InitialCoursesUnitsStates } from '../../../@types/initialCoursesUnitsStates';
import { AppDispatch } from 'redux/stores/initialStore';

const initialState: InitialCoursesUnitsStates = {
  coursesUnitsList: { items: [], page: 1, pageSize: 20, total: 0, totalPages: 0 },
  coursesUnitsSelected: [],
  dataIsLoading: false,
  currentCoursesUnitsSelectedId: '',
  currentCoursesUnitsScreen: CoursesUnitsScreen.coursesUnits,
  currentCoursesUnitsStructure: {
    id: '',
    courseUnitNovadisId: '',
    name: '',
    createAt: '',
    updateAt: '',
    chapters: [],
    nbchapters: 0,
  },
  coursesUnitsChaptersSelected: [],
  currentChapterSelectedId: '',
  coursesUnitsContentSelected: [],
  currentCourseSelectedId: '',
  coursesContentSelected: [],
  courseSelectedId: '',
  coursesContentsList: {
    items: [],
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  },
  listOfLearningSubjects: [],
  coursesUnitsSearch: '',
  currentChapterSearch: '',
  coursesUnitsContentSearch: '',
  coursesContentSearch: '',
  currentProvider: ExternalContentProvider.Apolearn,
  breadcrumb: '',
  currentCourseUnitColumnKeySort: 'name',
  currentCourseUnitColumnSortSens: 'asc',
  currentCourseContentColumnKeySort: 'name',
  currentCourseContentColumnSortSens: 'asc',
  courseUnitGoToTop: false,
  courseContentGoToTop: false,
  chooseCourseContent: false,
  importSearch: '',
  classroomsList: { items: [], page: 0, pageSize: 0, total: 0, totalPages: 0 },
  importProvider: ImportProvider.Apolearn,
  courseContentProvider: ContentProvider.Apolearn,
  linkMode: false,
  currentPageSearch: 1,
};

const slice = createSlice({
  name: 'projet',
  initialState,
  reducers: {
    setImportSearch(state, action: PayloadAction<string>) {
      state.importSearch = action.payload;
    },
    setCurrentCourseUnitColumnKeySort(state, action: PayloadAction<string>) {
      state.currentCourseUnitColumnKeySort = action.payload;
    },

    setCurrentCourseUnitColumnSortSens(state, action: PayloadAction<string>) {
      state.currentCourseUnitColumnSortSens = action.payload;
    },
    setCurrentCourseContentColumnKeySort(state, action: PayloadAction<string>) {
      state.currentCourseContentColumnKeySort = action.payload;
    },
    setCurrentCourseContentColumnSortSens(state, action: PayloadAction<string>) {
      state.currentCourseContentColumnSortSens = action.payload;
    },
    setCoursesUnitsSearch(state, action: PayloadAction<string>) {
      state.coursesUnitsSearch = action.payload;
    },
    setCurrentChapterSearch(state, action: PayloadAction<string>) {
      state.currentChapterSearch = action.payload;
    },
    setCoursesUnitsContentSearch(state, action: PayloadAction<string>) {
      state.coursesUnitsContentSearch = action.payload;
    },
    setCoursesContentSearch(state, action: PayloadAction<string>) {
      state.coursesContentSearch = action.payload;
    },
    setDataIsLoading(state, action: PayloadAction<boolean>) {
      state.dataIsLoading = action.payload;
    },
    setLinkMode(state, action: PayloadAction<boolean>) {
      state.linkMode = action.payload;
    },
    setCoursesUnitsList(state, action: PayloadAction<CoursesUnitsList>) {
      state.coursesUnitsList = action.payload;
    },

    setClassroomsList(state, action: PayloadAction<ClassroomsList>) {
      state.classroomsList = action.payload;
    },

    setCoursesUnitsSelected(state, action: PayloadAction<string[]>) {
      state.coursesUnitsSelected = action.payload;
    },
    setCoursesUnitsChaptersSelected(state, action: PayloadAction<string[]>) {
      state.coursesUnitsChaptersSelected = action.payload;
    },
    setCurrentCoursesUnitsSelectedId(state, action: PayloadAction<string>) {
      state.currentCoursesUnitsSelectedId = action.payload;
    },
    setCurrentChapterSelectedId(state, action: PayloadAction<string>) {
      state.currentChapterSelectedId = action.payload;
    },
    setCurrentCoursesUnitsScreen(state, action: PayloadAction<CoursesUnitsScreen>) {
      state.currentCoursesUnitsScreen = action.payload;
    },
    setCurrentCoursesUnitsStructure(state, action: PayloadAction<CourseUnit>) {
      state.currentCoursesUnitsStructure = action.payload;
    },
    setCoursesUnitsContentSelected(state, action: PayloadAction<string[]>) {
      state.coursesUnitsContentSelected = action.payload;
    },
    setCurrentCourseSelectedId(state, action: PayloadAction<string>) {
      state.currentCourseSelectedId = action.payload;
    },
    setCoursesContentSelected(state, action: PayloadAction<string[]>) {
      state.coursesContentSelected = action.payload;
    },
    setCourseSelectedId(state, action: PayloadAction<string>) {
      state.courseSelectedId = action.payload;
    },
    setCoursesContentsList(state, action: PayloadAction<CourseContentList>) {
      state.coursesContentsList = action.payload;
    },
    setListOfLearningSubjects(state, action: PayloadAction<TableKeyValue[]>) {
      state.listOfLearningSubjects = action.payload;
    },
    setCurrentProvider(state, action: PayloadAction<ExternalContentProvider>) {
      state.currentProvider = action.payload;
    },
    setImportProvider(state, action: PayloadAction<ImportProvider>) {
      state.importProvider = action.payload;
    },
    setCourseContentProvider(state, action: PayloadAction<ContentProvider>) {
      state.courseContentProvider = action.payload;
    },
    setBreadcrumb(state, action: PayloadAction<string>) {
      state.breadcrumb = action.payload;
    },
    setCourseUnitGoToTop(state, action: PayloadAction<boolean>) {
      state.courseUnitGoToTop = action.payload;
    },
    setCourseContentGoToTop(state, action: PayloadAction<boolean>) {
      state.courseContentGoToTop = action.payload;
    },
    setChooseCourseContent(state, action: PayloadAction<boolean>) {
      state.chooseCourseContent = action.payload;
    },
    setCurrentPageSearch(state, action: PayloadAction<number>) {
      state.currentPageSearch = action.payload;
    },
  },
});

export default slice.reducer;

export const {
  setCoursesUnitsList,
  setCoursesUnitsSelected,
  setDataIsLoading,
  setCurrentCoursesUnitsSelectedId,
  setCurrentCoursesUnitsScreen,
  setCurrentCoursesUnitsStructure,
  setCoursesUnitsChaptersSelected,
  setCurrentChapterSelectedId,
  setCoursesUnitsContentSelected,
  setCurrentCourseSelectedId,
  setCoursesContentSelected,
  setCourseSelectedId,
  setCoursesContentsList,
  setListOfLearningSubjects,
  setCoursesUnitsSearch,
  setCurrentChapterSearch,
  setCoursesUnitsContentSearch,
  setCoursesContentSearch,
  setCurrentProvider,
  setBreadcrumb,
  setCurrentCourseUnitColumnKeySort,
  setCurrentCourseUnitColumnSortSens,
  setCurrentCourseContentColumnKeySort,
  setCurrentCourseContentColumnSortSens,
  setCourseUnitGoToTop,
  setCourseContentGoToTop,
  setChooseCourseContent,
  setImportSearch,
  setClassroomsList,
  setImportProvider,
  setCourseContentProvider,
  setLinkMode,
  setCurrentPageSearch
} = slice.actions;

export const SetImportSearch = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setImportSearch(value));
};

export const SetChooseCourseContent = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(setChooseCourseContent(value));
};

export const SetLinkMode = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(setLinkMode(value));
};

export const SetCourseUnitGoToTop = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(setCourseUnitGoToTop(value));
};

export const SetCourseContentGoToTop = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(setCourseContentGoToTop(value));
};

export const SetCurrentCourseUnitColumnKeySort = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentCourseUnitColumnKeySort(value));
};

export const SetCurrentCourseUnitColumnSortSens = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentCourseUnitColumnSortSens(value));
};
export const SetCurrentCourseContentColumnKeySort = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentCourseContentColumnKeySort(value));
};
export const SetCurrentCourseContentColumnSortSens = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentCourseContentColumnSortSens(value));
};

export const SetCoursesUnitsSearch = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCoursesUnitsSearch(value));
};

export const SetBreadcrumb = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setBreadcrumb(value));
};

export const SetCurrentChapterSearch = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentChapterSearch(value));
};

export const SetCoursesUnitsContentSearch = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCoursesUnitsContentSearch(value));
};

export const SetCoursesContentSearch = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCoursesContentSearch(value));
};

export const SetDataIsLoading = (value: boolean) => (dispatch: AppDispatch) => {
  dispatch(setDataIsLoading(value));
};

export const SetCurrentCoursesUnitsScreen =
  (value: CoursesUnitsScreen) => (dispatch: AppDispatch) => {
    dispatch(setCurrentCoursesUnitsScreen(value));
  };

export const SetCurrentCoursesUnitsSelectedId = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentCoursesUnitsSelectedId(value));
};

export const SetListOfLearningSubjects = (value: TableKeyValue[]) => (dispatch: AppDispatch) => {
  dispatch(setListOfLearningSubjects(value));
};

export const SetCurrentCourseSelectedId = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentCourseSelectedId(value));
};

export const SetCurrentChapterSelectedId = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCurrentChapterSelectedId(value));
};

export const SetCurrentCoursesUnitsStructure = (value: CourseUnit) => (dispatch: AppDispatch) => {
  try {
    dispatch(setCurrentCoursesUnitsStructure(value));
    return true; // tout s'est bien passé
  } catch (error) {
    console.error('Erreur lors du dispatch :', error);
    return false;
  }
};

export const SetCoursesUnitsList = (value: CoursesUnitsList) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setCoursesUnitsList(value));
    return true; // tout s'est bien passé
  } catch (error) {
    console.error('Erreur lors du dispatch :', error);
    return false;
  }
};

export const SetClassroomsList = (value: ClassroomsList) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setClassroomsList(value));
    return true; // tout s'est bien passé
  } catch (error) {
    console.error('Erreur lors du dispatch :', error);
    return false;
  }
};

export const SetCoursesContentsList =
  (value: CourseContentList) => async (dispatch: AppDispatch) => {
    try {
      dispatch(setCoursesContentsList(value));
      return true; // tout s'est bien passé
    } catch (error) {
      console.error('Erreur lors du dispatch :', error);
      return false;
    }
  };

export const SetCoursesUnitsSelected = (value: string[]) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setCoursesUnitsSelected(value));
    return true; // tout s'est bien passé
  } catch (error) {
    console.error('Erreur lors du dispatch :', error);
    return false;
  }
};

export const SetCoursesUnitsContentSelected =
  (value: string[]) => async (dispatch: AppDispatch) => {
    try {
      dispatch(setCoursesUnitsContentSelected(value));
      return true; // tout s'est bien passé
    } catch (error) {
      console.error('Erreur lors du dispatch :', error);
      return false;
    }
  };

export const SetCoursesUnitsChaptersSelected =
  (value: string[]) => async (dispatch: AppDispatch) => {
    try {
      dispatch(setCoursesUnitsChaptersSelected(value));
      return true; // tout s'est bien passé
    } catch (error) {
      console.error('Erreur lors du dispatch :', error);
      return false;
    }
  };

export const SetCoursesContentSelected = (value: string[]) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setCoursesContentSelected(value));
    return true; // tout s'est bien passé
  } catch (error) {
    console.error('Erreur lors du dispatch :', error);
    return false;
  }
};

export const SetCourseSelectedId = (value: string) => (dispatch: AppDispatch) => {
  dispatch(setCourseSelectedId(value));
};

export const SetCurrentProvider = (value: ExternalContentProvider) => (dispatch: AppDispatch) => {
  dispatch(setCurrentProvider(value));
};

export const SetImportProvider = (value: ImportProvider) => (dispatch: AppDispatch) => {
  dispatch(setImportProvider(value));
};

export const SetCourseContentProvider = (value: ContentProvider) => (dispatch: AppDispatch) => {
  dispatch(setCourseContentProvider(value));
};

export const SetCurrentPageSearch = (value: number) => (dispatch: AppDispatch) => {
  dispatch(setCurrentPageSearch(value));
};
